/**
 * User preferences for the patient/caregiver profile windows.
 *
 * These live in localStorage rather than on the server because the AngularJS
 * shell has to read them synchronously, before any request goes out: `config.js`
 * decides which window template to open (and whether to run the deep-link
 * loading optimizations) while the router transition is still resolving, so an
 * async source would be too late.
 *
 * Every default here reproduces the legacy AngularJS behaviour — the legacy
 * window, no persisted fold state, no calendar hover indication, and the window
 * always opening on its first section. Turning `useBetaWindow` off is therefore
 * equivalent to the app as it behaved before these settings existed, which is
 * why the settings modal disables the rest of the options in that state: they
 * only describe the beta window.
 */

export type ProfileWindowEntity = "caregiver" | "patient";

/**
 * Sentinel for "reopen whichever section was open last" — stored in the same
 * field as a section label, which no section can collide with because section
 * labels are capitalised display strings ("Calendar", "Profile", ...).
 */
export const LAST_OPENED_SECTION = "last";

export type ProfileWindowSettings = {
  /** Master switch. `false` (default) keeps the legacy AngularJS window. */
  useBetaWindow: boolean;
  /** Persist each subsection's collapsed/expanded state across openings. */
  saveSubsectionFold: boolean;
  /** Keep calendar selections while navigating within an open profile. */
  saveCalendarSelection: boolean;
  /** Section label to open the caregiver window on, or `LAST_OPENED_SECTION`. */
  openCaregiverWindowOnSection: string;
  /** Section label to open the patient window on, or `LAST_OPENED_SECTION`. */
  openPatientWindowOnSection: string;
  /** Blue crosshair highlight on the calendar grid row/column under the cursor. */
  showCalendarHoverIndication: boolean;
};

/**
 * Both windows list "Calendar" first and open on it, so that is the legacy
 * default rather than `LAST_OPENED_SECTION`.
 */
export const DEFAULT_PROFILE_WINDOW_SECTION = "Calendar";

export const defaultProfileWindowSettings: ProfileWindowSettings = {
  useBetaWindow: false,
  saveSubsectionFold: false,
  saveCalendarSelection: true,
  openCaregiverWindowOnSection: DEFAULT_PROFILE_WINDOW_SECTION,
  openPatientWindowOnSection: DEFAULT_PROFILE_WINDOW_SECTION,
  showCalendarHoverIndication: false,
};

const SETTINGS_STORAGE_KEY = "medflyt:profile-window-settings";
const LAST_SECTION_STORAGE_KEY = "medflyt:profile-window-last-section";
const SUBSECTION_FOLD_STORAGE_KEY = "medflyt:profile-window-subsection-fold";

/** Fired on same-tab writes; cross-tab writes arrive as a native `storage` event. */
const CHANGE_EVENT = "medflyt:profile-window-settings-change";

function readJson(key: string): unknown {
  try {
    const raw = window.localStorage.getItem(key);

    return raw === null ? undefined : JSON.parse(raw);
  } catch {
    // Unreadable or malformed storage falls back to defaults rather than
    // breaking the window that is trying to open.
    return undefined;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Quota or private-mode failures are non-fatal: the setting just will not
    // survive the reload.
    return;
  }

  window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function coerceBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function coerceSection(value: unknown, fallback: string): string {
  return typeof value === "string" && value !== "" ? value : fallback;
}

function parseSettings(value: unknown): ProfileWindowSettings {
  if (!isRecord(value)) {
    return defaultProfileWindowSettings;
  }

  return {
    useBetaWindow: coerceBoolean(value.useBetaWindow, defaultProfileWindowSettings.useBetaWindow),
    saveSubsectionFold: coerceBoolean(
      value.saveSubsectionFold,
      defaultProfileWindowSettings.saveSubsectionFold
    ),
    saveCalendarSelection: coerceBoolean(
      value.saveCalendarSelection,
      defaultProfileWindowSettings.saveCalendarSelection
    ),
    openCaregiverWindowOnSection: coerceSection(
      value.openCaregiverWindowOnSection,
      defaultProfileWindowSettings.openCaregiverWindowOnSection
    ),
    openPatientWindowOnSection: coerceSection(
      value.openPatientWindowOnSection,
      defaultProfileWindowSettings.openPatientWindowOnSection
    ),
    showCalendarHoverIndication: coerceBoolean(
      value.showCalendarHoverIndication,
      defaultProfileWindowSettings.showCalendarHoverIndication
    ),
  };
}

/**
 * `useSyncExternalStore` compares snapshots by identity, so parsing storage on
 * every call would loop forever. The parsed value is cached against the raw
 * string it came from and only re-parsed when that string actually changes.
 */
let cachedRaw: string | null | undefined;
let cachedSettings: ProfileWindowSettings = defaultProfileWindowSettings;

export function getProfileWindowSettings(): ProfileWindowSettings {
  let raw: string | null = null;

  try {
    raw = window.localStorage.getItem(SETTINGS_STORAGE_KEY);
  } catch {
    return defaultProfileWindowSettings;
  }

  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedSettings = parseSettings(readJson(SETTINGS_STORAGE_KEY));
  }

  return cachedSettings;
}

export function setProfileWindowSettings(settings: ProfileWindowSettings): void {
  writeJson(SETTINGS_STORAGE_KEY, settings);
}

export function subscribeToProfileWindowSettings(onChange: () => void): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === SETTINGS_STORAGE_KEY) {
      onChange();
    }
  };

  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", handleStorage);

  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", handleStorage);
  };
}

/**
 * True when the beta window should be used. Kept as its own export because the
 * AngularJS shell asks this question in several places and should not have to
 * know the shape of the settings object.
 */
export function isBetaProfileWindowEnabled(): boolean {
  return getProfileWindowSettings().useBetaWindow;
}

/* -------------------------------------------------------------------------- */
/* Last opened section                                                        */
/* -------------------------------------------------------------------------- */

type LastOpenedSections = Partial<Record<ProfileWindowEntity, string>>;

function readLastOpenedSections(): LastOpenedSections {
  const value = readJson(LAST_SECTION_STORAGE_KEY);

  if (!isRecord(value)) {
    return {};
  }

  const result: LastOpenedSections = {};

  if (typeof value.caregiver === "string") {
    result.caregiver = value.caregiver;
  }

  if (typeof value.patient === "string") {
    result.patient = value.patient;
  }

  return result;
}

export function getLastOpenedSection(entity: ProfileWindowEntity): string | undefined {
  return readLastOpenedSections()[entity];
}

/**
 * Recorded on every section change, not only when the "last" option is active,
 * so switching the option on later still has somewhere to resume from.
 */
export function setLastOpenedSection(entity: ProfileWindowEntity, sectionLabel: string): void {
  const current = readLastOpenedSections();

  if (current[entity] === sectionLabel) {
    return;
  }

  writeJson(LAST_SECTION_STORAGE_KEY, { ...current, [entity]: sectionLabel });
}

/**
 * The section label a window should open on: the configured one, or the last
 * visited one when the "last" sentinel is stored. Returns `undefined` when the
 * answer is "whatever the window would have picked anyway" (i.e. its first
 * section), including when the remembered section no longer exists.
 */
export function resolveInitialSection(
  entity: ProfileWindowEntity,
  availableSectionLabels: string[]
): string | undefined {
  const settings = getProfileWindowSettings();
  const configured =
    entity === "caregiver"
      ? settings.openCaregiverWindowOnSection
      : settings.openPatientWindowOnSection;
  const target = configured === LAST_OPENED_SECTION ? getLastOpenedSection(entity) : configured;

  return target !== undefined && availableSectionLabels.includes(target) ? target : undefined;
}

/* -------------------------------------------------------------------------- */
/* Subsection fold state                                                      */
/* -------------------------------------------------------------------------- */

type SubsectionFoldState = Partial<Record<ProfileWindowEntity, Record<string, boolean>>>;

function readSubsectionFoldState(): SubsectionFoldState {
  const value = readJson(SUBSECTION_FOLD_STORAGE_KEY);

  return isRecord(value) ? (value as SubsectionFoldState) : {};
}

/**
 * `true` when the subsection was left collapsed. Always `false` while
 * `saveSubsectionFold` is off, so the stored state is ignored rather than
 * deleted — turning the option back on restores what was there.
 */
export function isSubsectionCollapsed(entity: ProfileWindowEntity, subsectionId: string): boolean {
  if (!getProfileWindowSettings().saveSubsectionFold) {
    return false;
  }

  return readSubsectionFoldState()[entity]?.[subsectionId] === true;
}

export function setSubsectionCollapsed(
  entity: ProfileWindowEntity,
  subsectionId: string,
  isCollapsed: boolean
): void {
  if (!getProfileWindowSettings().saveSubsectionFold) {
    return;
  }

  const current = readSubsectionFoldState();
  const forEntity = current[entity] ?? {};

  if (forEntity[subsectionId] === isCollapsed) {
    return;
  }

  writeJson(SUBSECTION_FOLD_STORAGE_KEY, {
    ...current,
    [entity]: { ...forEntity, [subsectionId]: isCollapsed },
  });
}
